import {
  DropDownButton,
  DropDownButtonItemClickEvent,
} from "@progress/kendo-react-buttons";
import axios, { AxiosError } from "axios";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import useElementLibrary from "hooks/useElementLibrary";
import { showApiErrorMessage, showErrorMessage } from "store/errorModalSlice";
import { selectRfi, setNewElements } from "store/rfiSlice";
import RfiElement from "types/RfiElement";
import { sectionNames } from "types/RfiTypes";

const ADD_FROM_LIBRARY = "From Library...";
const ADD_FROM_NEW = "New Element";

export interface IAddElementButtonProps {
  rfiId: number;
  section?: string;
  modifyElements?: (modifyAction: () => Promise<void>) => Promise<void>;
  targetElementId?: number;
  inElementStyle?: boolean;
  setIsModifying: any;
}

const AddElementButton = (props: IAddElementButtonProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const { openElementLibraryModal } = useElementLibrary();
  const rfi = useAppSelector(selectRfi);

  // Handlers
  const addLibraryElement = async (
    elementId: number,
    targetElementId?: number,
    position = "after"
  ) => {
    props.setIsModifying(true);
    if (elementId > 0) {
      await axios
        .post(`/api/v1/elements/library/${elementId}/copy`, {
          rfiId: props.rfiId,
          sectionGroupName: props.section,
          targetElementId: targetElementId,
          position: position,
        })
        .then(async (res: any) => {
          await axios
            .get(`/api/v1/rfi/${rfi.rfi?.rfiId}/elements/${props.section}`)
            .then((resElements) => {
              dispatch(
                setNewElements({
                  elements: resElements.data,
                  section: props.section ?? "",
                })
              );
              if (props.section === sectionNames.VENDOR_INFO) {
                dispatch(
                  setNewElements({
                    elements: resElements.data,
                    section: sectionNames.QUESTIONNAIRE,
                  })
                );
              }
              if (props.section === sectionNames.QUESTIONNAIRE) {
                dispatch(
                  setNewElements({
                    elements: resElements.data,
                    section: sectionNames.VENDOR_INFO,
                  })
                );
              }
              return resElements.data;
            })
            .catch((error:  Error | AxiosError) => {
              console.log(error);
              dispatch(
                showErrorMessage(
                  `There was an error copying element - ${error}`
                )
              );
            });

          return res.data;
        })
        .catch((error:  Error | AxiosError) => {
          console.log(error);
          dispatch(showApiErrorMessage({ errorResponse: error }));
        })
        .finally(() => {
          props.setIsModifying(false);
        });
    } else {
      dispatch(showErrorMessage(`Error adding new element - refresh error`));
    }
  };

  const addNewElement = async (
    targetElementId?: number,
    position: string = "after"
  ) => {
    props.setIsModifying(true);
    const rfiSection = {
      sectionGroupName: props.section,
      targetElementId: props.targetElementId ?? null,
      position: position,
    };

    const newElement = {
      elementId: 0,
      elementType:
        props.section === sectionNames.VENDOR_INFO ||
        props.section === sectionNames.QUESTIONNAIRE
          ? "survey"
          : "field",
      fieldType: "textfield",
      fieldLabel: "New Field Label",
      editLabel: true,
      requiredResponse: false,
      customerRequired: false,
      editRequired: true,
      rfiId: props.rfiId,
      isDirty: false,
      requiredInRfi: false,
    } as RfiElement;

    await axios
      .post(`/api/v1/rfi/${props.rfiId}/elements`, {
        section: rfiSection,
        element: newElement,
      })
      .then(async (res: any) => {
        // After adding get new state from server
        await axios
          .get(`/api/v1/rfi/${rfi.rfi?.rfiId}/elements/${props.section}`)

          // Update RFI state
          .then((resElements) => {
            dispatch(
              setNewElements({
                elements: resElements.data,
                section: props.section ?? "",
              })
            );
            dispatch(
              setNewElements({
                elements: resElements.data,
                section: props.section ?? "",
              })
            );
            // If vendor info or questionnaire, update the other so question numbers are correct
            if (props.section === sectionNames.VENDOR_INFO) {
              dispatch(
                setNewElements({
                  elements: resElements.data,
                  section: sectionNames.QUESTIONNAIRE,
                })
              );
            }
            if (props.section === sectionNames.QUESTIONNAIRE) {
              dispatch(
                setNewElements({
                  elements: resElements.data,
                  section: sectionNames.VENDOR_INFO,
                })
              );
            }
            return resElements.data;
          })
          .catch((error:  Error | AxiosError) => {
            console.log(error);
            dispatch(
              showErrorMessage(`There was an error copying element - ${error}`)
            );
          });
        return res.data;
      })
      .catch((error:  Error | AxiosError) => {
        console.log(error);
        dispatch(
          showErrorMessage(`There was an error updating element - ${error}`)
        );
      })
      .finally(() => {
        props.setIsModifying(false);
      });
  };

  // Handle Add DropDownButton
  const handleAddDropdown = async (event: DropDownButtonItemClickEvent) => {
    // Add From Library
    if (event.item === ADD_FROM_LIBRARY) {
      const isSelected = await openElementLibraryModal({
        elementTypeFilter:
          props.section === sectionNames.VENDOR_INFO ||
          props.section === sectionNames.QUESTIONNAIRE
            ? "survey"
            : "field",
      });

      if (isSelected && typeof isSelected === "number") {
        addLibraryElement(isSelected, props.targetElementId);
      }
    }
    // Add New Element
    else if (event.item === ADD_FROM_NEW) {
      await addNewElement();
    }
  };

  if (props.inElementStyle) {
    return (
      <DropDownButton
        text="Add"
        size="small"
        themeColor="primary"
        buttonClass="btn btn-primary"
        popupSettings={{
          popupClass: "k-toolbar-popup",
        }}
        items={[ADD_FROM_LIBRARY, ADD_FROM_NEW]}
        onItemClick={handleAddDropdown}
      />
    );
  }
  // Add on bottom
  else {
    return (
      <DropDownButton
        text={"Add"}
        size="medium"
        // rounded="full"
        // themeColor={"primary"}
        // className={"k-toolbar-dropdown"}
        buttonClass="btn btn-primary m-1"
        popupSettings={{
          popupClass: "k-toolbar-popup",
        }}
        items={[ADD_FROM_LIBRARY, ADD_FROM_NEW]}
        onItemClick={handleAddDropdown}
      />
    );
  }
};

export default AddElementButton;
